import { AUTHENTICATION_V2_ENDPOINT } from '@dtx-company/true-common/src/constants/endpoints'
import { FLOWCODE_ROOT } from '@dtx-company/true-common/src/constants/root'
import { Routes } from '@dtx-company/true-common/src/constants/routes'
import { useAuthState } from '../../../hooks/useAuthState'
import { useEffect, useMemo, useState } from 'react'
import { usePrelogin } from '../../../hooks/auth/usePrelogin'

function buildUrl(invitationLinkId: string, orgName?: string, ssoDomain?: string): string {
  const pathname = ssoDomain
    ? Routes.SIGN_IN
    : `/accept-org-invite/${encodeURIComponent(invitationLinkId)}`
  const url = new URL(pathname, FLOWCODE_ROOT)
  if (orgName) url.searchParams.append('org', orgName)
  if (ssoDomain) url.searchParams.append('ssoDomain', ssoDomain)
  else url.searchParams.append('userExists', 'false')
  return url.toString()
}

export function useInvitationLink(args: {
  orgId?: string
  linkType: 'team' | 'org'
  teamId?: string
}): string | null {
  const authState = useAuthState()
  const { token, org } = authState
  const userEmail = authState.jwt?.email
  const { checkIfEmailSSOEnabled, emailIsSSOEnabled, loading: ssoCheckLoading } = usePrelogin()
  const [invitation, setInvitation] = useState<null | { invitationLinkId: string }>(null)
  const [invitationLoading, setInvitationLoading] = useState(false)

  const ssoDomain = useMemo(() => {
    if (!emailIsSSOEnabled) return undefined
    if (!userEmail) return undefined
    // chop everything up to the @ to get the domain portion
    return userEmail.replace(/^.+@/, '')
  }, [emailIsSSOEnabled, userEmail])

  // Check for SSO
  useEffect(() => {
    if (!userEmail) return
    checkIfEmailSSOEnabled(userEmail)
  }, [checkIfEmailSSOEnabled, userEmail])

  // Fetch invitation link id
  useEffect(() => {
    const idForLinkType = args.linkType === 'team' ? args.teamId : args.orgId
    if (!invitation && idForLinkType) {
      setInvitationLoading(true)
      fetch(
        `${AUTHENTICATION_V2_ENDPOINT}/${args.linkType}/${idForLinkType ?? ''}/invitation-link`,
        {
          headers: {
            authorization: `Bearer ${token}`
          }
        }
      )
        .then(res => {
          if (res.status !== 200) {
            throw new Error('Failed to fetch invitation link')
          }
          return res.json()
        })
        .then(res => setInvitation(res))
        .catch(e => console.error(e))
        .finally(() => setInvitationLoading(false))
    }
  }, [invitation, setInvitation, args, token])

  return useMemo(() => {
    if (!invitation) return null
    if (invitationLoading || ssoCheckLoading) return null
    return buildUrl(invitation.invitationLinkId, org?.name, ssoDomain)
  }, [invitation, invitationLoading, ssoCheckLoading, org?.name, ssoDomain])
}
